import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "../../lib/utils";
import { RadioChip, type RadioChipSize, RadioGroup } from "../../primitives";

export type YesNoProps = {
	/** Current selection. `null` is the unselected state. */
	value: boolean | null;
	onChange: (value: boolean | null) => void;
	/** Optional label rendered above the chips. */
	label?: ReactNode;
	/** When true, clicking the selected chip un-selects it back to `null`. */
	deselectable?: boolean;
	disabled?: boolean;
	/**
	 * Chip dimensions. `md` (default) matches default form fields; `compact` matches a
	 * compact form input; `sm` is the metric-card chip.
	 */
	size?: RadioChipSize;
	className?: string;
	testId?: string;
	/** Analytics identifier — expands to `data-pendo-id` + `data-amp-track-id`. */
	trackingId?: string;
};

const stopEnter = (e: KeyboardEvent) => {
	if (e.key === "Enter") e.stopPropagation();
};

export const YesNo = ({
	value,
	onChange,
	label,
	deselectable = false,
	disabled,
	size = "md",
	className,
	testId,
	trackingId,
}: YesNoProps) => {
	const group = (
		<RadioGroup<boolean | null>
			value={value}
			onChange={onChange}
			disabled={disabled}
			onKeyDown={stopEnter}
			gap="sm"
			className={cn(label ? undefined : className)}
			data-testid={testId}
			trackingId={trackingId}
		>
			<RadioChip
				size={size}
				value={true}
				onClick={() => {
					if (disabled) return;
					if (deselectable && value === true) onChange(null);
				}}
				data-testid={testId ? `${testId}-yes-button` : undefined}
			>
				Yes
			</RadioChip>
			<RadioChip
				size={size}
				value={false}
				onClick={() => {
					if (disabled) return;
					if (deselectable && value === false) onChange(null);
				}}
				data-testid={testId ? `${testId}-no-button` : undefined}
			>
				No
			</RadioChip>
		</RadioGroup>
	);

	if (label === undefined) return group;

	return (
		<div className={cn("flex flex-col gap-2", className)}>
			<span className="text-xs font-medium text-secondary">{label}</span>
			{group}
		</div>
	);
};
